import { Link, useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import {
  LayoutDashboard,
  Package,
  Boxes,
  GitBranch,
  Calendar,
  MapPin,
  Wrench,
  AlertTriangle,
  ClipboardCheck,
  FileText,
  Shield,
  ShieldCheck,
  Users,
  User,
  Briefcase,
  Kanban,
  DollarSign,
  Clock,
  Receipt,
  Car,
  Coffee,
  Calculator,
  BarChart3,
  Tag,
  HardHat,
  UserPlus,
  KeyRound,
  ScrollText,
  Building2,
  Blocks,
  Activity,
  Cog,
  Home,
  Megaphone,
  Wand2,
  Settings2,
  Archive,
  CalendarClock,
  LockKeyhole,
  ClipboardList,
  ShoppingCart,
  Truck,
} from 'lucide-react';
import { cn } from '@/app/components/ui/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { useAlertsSummary } from '@/hooks/use-platform';
import { usePlatformModules } from '@/hooks/use-modules';
import { usePendingRequisitionCount } from '@/hooks/use-procurement';
import { useRequisitionPermissions } from '@/hooks/use-requisition-permissions';
import { PURCHASING_MODULE_KEY, REQUISITION_MODULE_KEYS } from '@/lib/purchasing';
import { useShowInvoicing } from '@/hooks/use-show-invoicing';
import { useConsultantOverview } from '@/hooks/use-management-reviews';
import { useSetupWizardProfile } from '@/hooks/use-setup-wizard';
import { FeedbackButton } from '@/app/components/navigation/feedback-button';
import { MODULE_NAVIGATION_ITEMS } from '@/app/components/navigation/primary-navigation';
import { useBranding } from '@/contexts/branding-context';
import { OrganizationLogo } from '@/app/components/navigation/organization-logo';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

function SidebarLink({
  item,
  isActive,
}: {
  item: NavItem;
  isActive: boolean;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      className={cn(
        'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300'
          : 'text-gray-700 dark:text-foreground hover:bg-gray-50 dark:hover:bg-accent hover:text-gray-900 dark:hover:text-foreground'
      )}
    >
      <Icon
        className={cn(
          'h-4 w-4 flex-shrink-0',
          isActive ? 'text-blue-700' : 'text-gray-400 dark:text-muted-foreground group-hover:text-gray-600 dark:group-hover:text-foreground'
        )}
      />
      <span className="flex-1">{item.name}</span>
      {item.badge !== undefined && item.badge > 0 && (
        <span className="ml-auto inline-flex items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold leading-none text-white">
          {item.badge}
        </span>
      )}
    </Link>
  );
}

function isItemActive(
  href: string,
  pathname: string,
  isDashboard: boolean,
  allHrefs?: string[],
): boolean {
  if (isDashboard) {
    return pathname === href;
  }
  const isMatch = pathname === href || pathname.startsWith(href + '/');
  if (!isMatch) return false;
  // If a more-specific sibling href also matches, this item is not the active one.
  if (allHrefs) {
    for (const other of allHrefs) {
      if (other !== href && other.startsWith(href + '/') && (pathname === other || pathname.startsWith(other + '/'))) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Requisitions nav item with a pending-approval badge for reviewers.
 * Shared by the H3Gear and purchasing (H3Books) sidebars.
 */
function useRequisitionsNavItem(): NavItem {
  const { canReview } = useRequisitionPermissions();
  const { isModuleEnabled } = usePlatformModules();
  const { hasModuleAccess } = usePermissions();
  const reachable = REQUISITION_MODULE_KEYS.some(
    (key) => isModuleEnabled(key) && hasModuleAccess(key),
  );
  const { count } = usePendingRequisitionCount(canReview && reachable);
  return { name: 'Requisitions', href: '/requisitions', icon: ClipboardList, badge: count };
}

// H3Gear sidebar
function useGearNav(): NavSection[] {
  const requisitions = useRequisitionsNavItem();
  return [
    {
      items: [
        { name: 'Home', href: '/gear', icon: Home },
        { name: 'Board', href: '/gear-requests/board', icon: Kanban },
        { name: 'Assets', href: '/assets', icon: Package },
        { name: 'Kits', href: '/kits', icon: Boxes },
        { name: 'Deployments', href: '/deployments', icon: GitBranch },
        { name: 'Reservations', href: '/reservations', icon: Calendar },
        { name: 'Maintenance', href: '/maintenance', icon: Wrench },
        { name: 'Compliance', href: '/gear/compliance', icon: AlertTriangle },
        requisitions,
      ],
    },
  ];
}

// H3Comply sidebar
function useComplyNav(isActive: boolean): NavSection[] {
  const { data: consultantData } = useConsultantOverview(isActive);
  const isMultiOrg = consultantData && !('error' in consultantData);
  const { data: wizardProfile } = useSetupWizardProfile(isActive);
  const wizardStatus = wizardProfile?.wizard_status ?? 'not_started';

  const items: NavItem[] = [
    { name: 'Dashboard', href: '/compliance', icon: LayoutDashboard },
    { name: 'Clauses', href: '/compliance/clauses', icon: ShieldCheck },
    { name: 'Documents', href: '/compliance/documents', icon: FileText },
    { name: 'Personnel', href: '/compliance/personnel', icon: Users },
    { name: 'Insurance', href: '/compliance/insurance', icon: Shield },
    { name: 'Incidents', href: '/compliance/incidents', icon: AlertTriangle },
    { name: 'Statistics', href: '/compliance/statistics', icon: BarChart3 },
    { name: 'KPIs', href: '/compliance/kpis', icon: Activity },
    { name: 'Audit Prep', href: '/compliance/audit-prep', icon: ClipboardCheck },
    { name: 'Reviews', href: '/compliance/reviews', icon: ScrollText },
    { name: 'Communications', href: '/compliance/communications', icon: Megaphone },
    { name: 'Actions', href: '/compliance/actions', icon: ClipboardCheck },
    { name: 'Alerts', href: '/compliance/alerts', icon: AlertTriangle },
  ];

  if (isMultiOrg) {
    items.push({ name: 'Consultant View', href: '/compliance/consultant', icon: Building2 });
  }

  if (wizardStatus !== 'completed') {
    items.push({ name: 'Setup Wizard', href: '/compliance/setup-wizard', icon: Wand2 });
  } else {
    items.push({ name: 'Edit Setup', href: '/compliance/setup-wizard', icon: Settings2 });
  }

  return [{ items }];
}

// H3Safe sidebar
function useSafeNav(): NavSection[] {
  return [
    {
      items: [
        { name: 'Home', href: '/safety', icon: Home },
        { name: 'JSAs', href: '/safety/jsas', icon: ClipboardCheck },
        { name: 'JSA Archive', href: '/safety/jsa-archive', icon: Archive },
        { name: 'Safe Work Plans', href: '/safety/work-plans', icon: FileText },
        { name: 'Rescue Plans', href: '/safety/rescue-plans', icon: Shield },
        { name: 'Toolbox Talks', href: '/safety/toolbox-talks', icon: Users },
        { name: 'Toolbox Analytics', href: '/safety/toolbox-analytics', icon: BarChart3 },
        { name: 'Near-Miss Reports', href: '/safety/near-misses', icon: AlertTriangle },
        { name: 'Templates', href: '/safety/templates', icon: FileText },
      ],
    },
  ];
}

// H3Crew sidebar
function useCrewNav(): NavSection[] {
  const permissions = usePermissions();
  return [
    {
      items: [
        { name: 'Home', href: '/crew', icon: Home },
        { name: 'Schedule', href: '/crew/schedule', icon: Calendar },
        { name: 'Technicians', href: '/crew/techs', icon: Users },
        ...(permissions.isAdmin || permissions.isManager
          ? [{ name: 'Team Status', href: '/admin', icon: BarChart3 }]
          : []),
        { name: 'Travel', href: '/crew/travel', icon: Car },
        { name: 'Cert Alerts', href: '/crew/certifications/expiring', icon: AlertTriangle },
        { name: 'Crew Lists', href: '/crew/lists', icon: ClipboardCheck },
        { name: 'My Availability', href: '/crew/my-availability', icon: Calendar },
      ],
    },
  ];
}

// H3Project sidebar
function useWorkNav(): NavSection[] {
  const { isModuleEnabled } = usePlatformModules();
  const { hasModuleAccess } = usePermissions();
  return [
    {
      items: [
        ...(isModuleEnabled('work') && hasModuleAccess('work')
          ? [{ name: 'Dashboard', href: '/work', icon: LayoutDashboard }]
          : []),
        { name: 'Daily Reports', href: '/work/daily-reports', icon: FileText },
      ],
    },
  ];
}

// H3Books sidebar
function useProjectNav(): NavSection[] {
  const showInvoicing = useShowInvoicing();
  const requisitions = useRequisitionsNavItem();
  const { canViewClients } = usePermissions();

  return [
    {
      items: [
        { name: 'Home', href: '/projects', icon: Home },
        { name: 'Projects', href: '/projects/list', icon: Briefcase },
        ...(canViewClients
          ? [{ name: 'Clients', href: '/clients', icon: Building2 }]
          : []),
        { name: 'Time Tracking', href: '/projects/time', icon: Clock },
        { name: 'Daily Reports', href: '/work/daily-reports', icon: FileText },
        ...(showInvoicing
          ? [
              { name: 'Bids', href: '/projects/bids', icon: FileText },
              { name: 'Invoices', href: '/projects/invoices', icon: Receipt },
            ]
          : []),
      ],
    },
    {
      title: 'Purchasing',
      items: [
        requisitions,
        { name: 'Purchase Orders', href: '/purchase-orders', icon: ShoppingCart },
        { name: 'Vendors', href: '/vendors', icon: Truck },
      ],
    },
  ];
}

// H3Pay sidebar
// Members (effective payroll role below manager) only get their own pages:
// Timesheets, Per Diem, Expenses, Mileage. Manager+ additionally gets Payroll
// Runs, Pay Rates, Job Costing, Cost Codes, Reports and Settings.
function usePayNav(): NavSection[] {
  const { canManagePayroll } = usePermissions();

  if (!canManagePayroll) {
    return [
      {
        items: [
          { name: 'Timesheets', href: '/payroll/timesheets', icon: Clock },
          { name: 'Per Diem', href: '/payroll/per-diem', icon: Coffee },
          { name: 'Expenses', href: '/payroll/expenses', icon: Receipt },
          { name: 'Mileage', href: '/payroll/mileage', icon: Car },
        ],
      },
    ];
  }

  return [
    {
      items: [
        { name: 'Home', href: '/payroll', icon: Home },
        { name: 'Payroll Runs', href: '/payroll/runs', icon: DollarSign },
        { name: 'Pay Rates', href: '/payroll/rates', icon: Tag },
        { name: 'Timesheets', href: '/payroll/timesheets', icon: Clock },
        { name: 'Expenses', href: '/payroll/expenses', icon: Receipt },
        { name: 'My Expenses', href: '/payroll/my-expenses', icon: Receipt },
        { name: 'Mileage', href: '/payroll/mileage', icon: Car },
        { name: 'Per Diem', href: '/payroll/per-diem', icon: Coffee },
        { name: 'Job Costing', href: '/payroll/job-costing', icon: Calculator },
        { name: 'Cost Codes', href: '/payroll/cost-codes', icon: Tag },
        { name: 'Reports', href: '/payroll/reports', icon: BarChart3 },
        { name: 'Settings', href: '/payroll/settings', icon: Settings2 },
      ],
    },
  ];
}

// Admin sidebar - role-based: everyone sees some items, admins see all
function useAdminNav(): NavSection[] {
  const permissions = usePermissions();
  const { isModuleEnabled } = usePlatformModules();
  const inventoryAccessible =
    isModuleEnabled('inventory') && permissions.hasModuleAccess('inventory');
  const { data: alertsSummary } = useAlertsSummary();

  return useMemo(() => {
    const sections: NavSection[] = [];
    const inventoryModuleOn = isModuleEnabled('inventory');

    // Overview section - visible to everyone; the org-wide Admin Dashboard
    // link is offered to org Admins only (the Admin group enters at People
    // for everyone else).
    const overviewItems: NavItem[] = [
      ...(permissions.isAdmin
        ? [{ name: 'Admin Dashboard', href: '/dashboard', icon: LayoutDashboard } as NavItem]
        : []),
      { name: 'Alerts', href: '/alerts', icon: AlertTriangle, badge: alertsSummary?.total_unread_alerts },
    ];

    // Projects & People - visible to everyone (Option A)
    overviewItems.push(
      { name: 'Projects', href: '/projects/list', icon: HardHat },
      { name: 'People', href: '/people', icon: Users },
      { name: 'Locations', href: '/locations', icon: MapPin },
      { name: 'Gear Lite', href: '/gear-lite', icon: Wrench },
      { name: 'Documents', href: '/documents', icon: FileText },
    );

    if (!inventoryModuleOn) {
      overviewItems.push({
        name: 'Upgrade to H3Gear',
        href: '/settings/subscription',
        icon: LockKeyhole,
      });
    }

    sections.push({ title: 'Overview', items: overviewItems });

    // Reports section - visible to inspectors and above, only when inventory module is enabled
    if (inventoryAccessible && (permissions.isAdmin || permissions.isManager || permissions.isSupervisor || permissions.isUser)) {
      sections.push({
        title: 'Reports',
        items: [
          { name: 'Inspections', href: '/inspections', icon: ClipboardCheck },
          { name: 'Utilization', href: '/reports/utilization', icon: BarChart3 },
          { name: 'Compliance', href: '/reports/inspection-compliance', icon: AlertTriangle },
          { name: 'Rope Cuts', href: '/reports/rope-cuts', icon: ScrollText },
          { name: 'Gear Forecast', href: '/reports/gear-forecast', icon: CalendarClock },
        ],
      });
    }

    // Platform Admin - staff only
    if (permissions.isPlatformStaff) {
      sections.push({
        title: 'Platform Admin',
        items: [
          { name: 'Organizations', href: '/platform-admin', icon: Building2 },
          { name: 'Gear Models', href: '/platform/gear-models', icon: Cog },
        ],
      });
    }

    // Account section - visible to all users
    sections.push({
      title: 'Account',
      items: [
        { name: 'My Profile', href: '/profile', icon: User },
      ],
    });

    // Organization settings - admin/manager only
    if (permissions.isAdmin || permissions.isManager) {
      const orgItems: NavItem[] = [];

      if (permissions.canManageOrganization) {
        orgItems.push(
          { name: 'Organization', href: '/settings', icon: Building2 },
          { name: 'Single Sign-On', href: '/settings/sso', icon: KeyRound },
        );
      }
      if (permissions.canManageOrganization) {
        orgItems.push({ name: 'Modules', href: '/settings/modules', icon: Blocks });
      }
      if (permissions.canManageUsers) {
        orgItems.push({ name: 'Users', href: '/settings/users', icon: Users });
      }
      if (permissions.canManageInvitations) {
        orgItems.push({ name: 'Invitations', href: '/settings/invitations', icon: UserPlus });
      }
      if (permissions.canManageTemplates && inventoryAccessible) {
        orgItems.push({ name: 'Asset Templates', href: '/settings/templates', icon: Package });
      }
      if (permissions.canViewAuditLogs) {
        orgItems.push({ name: 'Audit Logs', href: '/settings/audit-logs', icon: ScrollText });
      }

      if (orgItems.length > 0) {
        sections.push({ title: 'Organization', items: orgItems });
      }
    }

    return sections;
  }, [permissions, isModuleEnabled, alertsSummary, inventoryAccessible]);
}

type ActiveModule = 'gear' | 'safety' | 'crew' | 'projects' | 'payroll' | 'work' | 'admin' | 'comply' | 'my-dashboard' | 'h3ightboard' | null;

/** Backend module key → the sidebar that hosts it. */
const SIDEBAR_FOR_MODULE_KEY: Record<string, ActiveModule> = {
  inventory: 'gear',
  safety: 'safety',
  crew: 'crew',
  projects: 'projects',
  payroll: 'payroll',
  work: 'work',
  compliance: 'comply',
};
const PURCHASING_SIDEBAR: ActiveModule = SIDEBAR_FOR_MODULE_KEY[PURCHASING_MODULE_KEY] ?? null;

function getActiveModule(
  pathname: string,
  isModuleEnabled: (key: string) => boolean,
  hasModuleAccess: (key: string) => boolean,
): ActiveModule {
  const inventoryEnabled =
    isModuleEnabled('inventory') && hasModuleAccess('inventory');
  if (pathname === '/h3ightboard' || pathname.startsWith('/h3ightboard/')) return 'h3ightboard';
  if (pathname === '/my-dashboard' || pathname.startsWith('/my-dashboard/')) return 'my-dashboard';
  // Requisitions sit in whichever module the user came from: H3Gear when it is
  // on (the supervisor "I need gear" path), otherwise the purchasing module.
  if (pathname === '/requisitions' || pathname.startsWith('/requisitions/')) {
    return inventoryEnabled ? 'gear' : PURCHASING_SIDEBAR;
  }
  if (
    pathname === '/purchase-orders' ||
    pathname.startsWith('/purchase-orders/') ||
    pathname === '/vendors' ||
    pathname.startsWith('/vendors/')
  ) return PURCHASING_SIDEBAR;
  if (
    pathname === '/gear' ||
    pathname.startsWith('/gear/') ||
    pathname.startsWith('/gear-requests') ||
    pathname.startsWith('/assets') ||
    pathname.startsWith('/kits') ||
    pathname.startsWith('/deployments') ||
    pathname.startsWith('/reservations') ||
    pathname.startsWith('/maintenance') ||
    pathname.startsWith('/gear/compliance')
  ) return 'gear';
  if (pathname === '/compliance' || pathname.startsWith('/compliance/')) return 'comply';
  if (pathname === '/safety' || pathname.startsWith('/safety/')) return 'safety';
  if (pathname === '/crew' || pathname.startsWith('/crew/')) return 'crew';
  if (pathname === '/projects' || pathname.startsWith('/projects/') || pathname === '/clients' || pathname.startsWith('/clients/')) {
    // Jobs are foundation records reachable without H3Books. Only show a
    // module sidebar the user can actually open (module on for the org and
    // effective role above no_access): Books first, then Crew, otherwise the
    // Admin group, whose Overview links Projects.
    const canUse = (key: string) => isModuleEnabled(key) && hasModuleAccess(key);
    if (canUse('projects')) return 'projects';
    return canUse('crew') ? 'crew' : 'admin';
  }
  if (pathname === '/work' || pathname.startsWith('/work/')) return 'work';
  if (pathname === '/payroll' || pathname.startsWith('/payroll/')) return 'payroll';
  if (
    pathname === '/dashboard' ||
    pathname === '/admin' ||
    pathname.startsWith('/admin/') ||
    pathname.startsWith('/settings') ||
    pathname === '/profile' ||
    pathname.startsWith('/profile/') ||
    pathname.startsWith('/people') ||
    pathname === '/locations' ||
    pathname.startsWith('/locations/') ||
    pathname === '/gear-lite' ||
    pathname.startsWith('/gear-lite/') ||
    pathname === '/documents' ||
    pathname.startsWith('/documents/') ||
    pathname.startsWith('/reports') ||
    pathname.startsWith('/inspections') ||
    pathname === '/alerts' ||
    pathname.startsWith('/alerts/') ||
    pathname.startsWith('/platform/')
  ) return 'admin';
  return null;
}

const MODULE_LABELS: Record<string, string> = {
  gear: 'Gear',
  safety: 'Safe',
  crew: 'Crew',
  projects: 'Books',
  payroll: 'Pay',
  work: 'Project',
  comply: 'Comply',
  admin: 'Admin',
};

interface ModuleSidebarProps {
  /** When true, removes responsive hiding classes (for use inside mobile Sheet) */
  mobile?: boolean;
}

export function ModuleSidebar({ mobile = false }: ModuleSidebarProps) {
  const location = useLocation();
  const { branding } = useBranding();
  const { isModuleEnabled } = usePlatformModules();
  const { isAdmin, hasModuleAccess } = usePermissions();
  const activeModule = getActiveModule(location.pathname, isModuleEnabled, hasModuleAccess);

  const gearNav = useGearNav();
  const complyNav = useComplyNav(activeModule === 'comply');
  const safeNav = useSafeNav();
  const crewNav = useCrewNav();
  const projectNav = useProjectNav();
  const workNav = useWorkNav();
  const payNav = usePayNav();
  const adminNav = useAdminNav();
  const mobilePrimaryLinks: NavItem[] = [
    { name: 'H3ightboard', href: '/h3ightboard', icon: Kanban },
    { name: 'My Dashboard', href: '/my-dashboard', icon: Home },
    ...MODULE_NAVIGATION_ITEMS
      .filter((item) => isModuleEnabled(item.moduleKey) && hasModuleAccess(item.moduleKey))
      .map((item) => ({
        name: item.label,
        href: item.href,
        icon: item.icon,
      })),
    // Every role keeps the Admin group (People, Documents, Alerts...);
    // only org Admins enter it at the org-wide Admin Dashboard.
    { name: 'Admin', href: isAdmin ? '/dashboard' : '/people', icon: Shield },
  ];

  // No sidebar for H3ightboard, My Dashboard, or unrecognized routes on desktop.
  // On mobile, show a fallback menu listing all module entry points
  // so users can navigate to modules not in the bottom nav.
  if (!activeModule || activeModule === 'my-dashboard' || activeModule === 'h3ightboard') {
    if (!mobile) return null;

    return (
      <aside className="flex flex-col w-56 border-r border-gray-200 dark:border-border bg-white dark:bg-card overflow-y-auto print:hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-border">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
            Modules
          </h2>
        </div>
        <nav className="flex-1 px-3 py-3 space-y-0.5">
          {mobilePrimaryLinks.map((item) => {
            const active = isItemActive(item.href, location.pathname, item.name === 'My Dashboard', mobilePrimaryLinks.map(i => i.href));
            return <SidebarLink key={item.href} item={item} isActive={active} />;
          })}
        </nav>
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-border px-3 py-3">
          <a
            href="https://h3ight.com/status"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-foreground hover:bg-gray-50 dark:hover:bg-accent rounded-lg transition-colors"
          >
            <Activity className="h-4 w-4 flex-shrink-0" />
            System Status
          </a>
          <FeedbackButton />
        </div>
      </aside>
    );
  }

  let sections: NavSection[];
  switch (activeModule) {
    case 'gear':
      sections = gearNav;
      break;
    case 'comply':
      sections = complyNav;
      break;
    case 'safety':
      sections = safeNav;
      break;
    case 'crew':
      sections = crewNav;
      break;
    case 'projects':
      sections = projectNav;
      break;
    case 'work':
      sections = workNav;
      break;
    case 'payroll':
      sections = payNav;
      break;
    case 'admin':
      sections = adminNav;
      break;
    default:
      return null;
  }

  const moduleLabel = MODULE_LABELS[activeModule];

  return (
    <aside className={cn(
      'flex flex-col w-56 border-r border-gray-200 dark:border-border bg-white dark:bg-card overflow-y-auto print:hidden',
      !mobile && 'hidden lg:flex'
    )}>
      <div className="flex items-center h-16 flex-shrink-0 px-4 border-b border-gray-200 dark:border-border">
        <OrganizationLogo
          logoUrl={branding?.logo_url}
          organizationName={branding?.name}
          variant="sidebar"
        />
      </div>

      {/* Module header */}
      {moduleLabel && (
        <div className="px-4 py-3 border-b border-gray-100 dark:border-border">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
            {moduleLabel}
          </h2>
        </div>
      )}

      {/* Navigation sections */}
      <nav className="flex-1 px-3 py-3 space-y-4">
        {mobile && (
          <div>
            <h3 className="px-3 mb-1 text-xs font-semibold text-gray-400 dark:text-muted-foreground uppercase tracking-wider">
              Modules
            </h3>
            <div className="space-y-0.5">
              {mobilePrimaryLinks.map((item) => {
                const active = isItemActive(
                  item.href,
                  location.pathname,
                  item.name === 'My Dashboard',
                  mobilePrimaryLinks.map((link) => link.href),
                );
                return (
                  <SidebarLink key={item.href} item={item} isActive={active} />
                );
              })}
            </div>
          </div>
        )}
        {sections.map((section, sIdx) => (
          <div key={sIdx}>
            {section.title && (
              <h3 className="px-3 mb-1 text-xs font-semibold text-gray-400 dark:text-muted-foreground uppercase tracking-wider">
                {section.title}
              </h3>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const isDashboard = item.name === 'Home' || item.name === 'Admin Dashboard';
                const sectionHrefs = section.items.map(i => i.href);
                const active = isItemActive(item.href, location.pathname, isDashboard, sectionHrefs);
                return (
                  <SidebarLink key={item.href} item={item} isActive={active} />
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 dark:border-border px-3 py-3">
        <a
          href="https://h3ight.com/status"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-foreground hover:bg-gray-50 dark:hover:bg-accent rounded-lg transition-colors"
        >
          <Activity className="h-4 w-4 flex-shrink-0" />
          System Status
        </a>
        <FeedbackButton />
      </div>
    </aside>
  );
}
